/**
 * ProductDetail (C-1a) — a single product in the Master Product Register.
 * Route: /regulatory/products/$id
 */
import { Link } from '@tanstack/react-router';
import styles from './Regulatory.module.css';
import { useProductDetailQuery, CATEGORY_LABEL, REGSTATUS_LABEL } from './useRegulatory';

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}

export function ProductDetail({ productId }: { productId: string }) {
  const q = useProductDetailQuery(productId);
  if (q.isLoading) return <main className={styles.page}><div className={styles.empty}>Loading product…</div></main>;
  if (q.isError || !q.data) return <main className={styles.page}><div className={styles.empty}>Product not found. <Link to="/regulatory/products">Back to register</Link></div></main>;
  const p = q.data;

  // Renewal banner: registered products whose 5-year certificate window closes within 12 months / has passed.
  let renewalState: 'ok' | 'soon' | 'expired' = 'ok';
  if (p.registrationStatus === 'REGISTERED' && p.renewalDueDate) {
    const due = new Date(p.renewalDueDate).getTime();
    const now = Date.now();
    if (due < now) renewalState = 'expired';
    else if (due < now + 365 * 24 * 3600 * 1000) renewalState = 'soon';
  }

  return (
    <main className={styles.page}>
      <nav className={styles.crumbs} aria-label="Breadcrumb">
        <Link to="/regulatory/products" className={styles.crumb}>Product Register</Link>
        <span className={styles.crumbSep} aria-hidden>/</span>
        <span>{p.productCode}</span>
      </nav>

      <header className={styles.detailHead}>
        <div>
          <h1 className={styles.detailTitle}>{p.brandName || p.itemDescription}</h1>
          <div className={styles.subline}>
            <span className={styles.mono}>{p.productCode}</span>
            <span className={`${styles.pill} ${p.category === 'PHARMA' ? styles.catPharma : p.category === 'NUTRA' ? styles.catNutra : styles.catMd}`}>{CATEGORY_LABEL[p.category] ?? p.category}</span>
            <span className={styles.statusPill}>{REGSTATUS_LABEL[p.registrationStatus] ?? p.registrationStatus}</span>
          </div>
        </div>
      </header>

      {renewalState !== 'ok' && (
        <div className={`${styles.renewBanner} ${renewalState === 'expired' ? styles.renewExpired : styles.renewSoon}`} role="status">
          {renewalState === 'expired'
            ? `⚠ Registration expired — certificate validity ended ${fmtDate(p.renewalDueDate)}. Renewal required.`
            : `⏳ Renewal due ${fmtDate(p.renewalDueDate)} (within 12 months). Plan the renewal dossier.`}
        </div>
      )}

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Product detail</h2>
        <dl className={styles.metaGrid}>
          <div><dt>Brand</dt><dd>{p.brandName ?? '—'}</dd></div>
          <div><dt>Molecule</dt><dd>{p.molecule ?? '—'}</dd></div>
          <div><dt>Strength</dt><dd>{p.strength ? `${p.strength} ${p.strengthUnit ?? ''}`.trim() : '—'}</dd></div>
          <div><dt>Pack size</dt><dd>{p.packSize ?? '—'}</dd></div>
          <div><dt>Registration #</dt><dd className={styles.mono}>{p.registrationNumber ?? '—'}</dd></div>
          <div><dt>Certificate issued</dt><dd>{fmtDate(p.certificateIssueDate)}</dd></div>
          <div><dt>Renewal due</dt><dd>{p.registrationStatus === 'REGISTERED' ? fmtDate(p.renewalDueDate) : '—'}</dd></div>
          <div><dt>Category</dt><dd>{CATEGORY_LABEL[p.category] ?? p.category}</dd></div>
        </dl>
        {p.itemDescription && <div className={styles.descBlock}><strong>Item description</strong><p>{p.itemDescription}</p></div>}
      </section>

      {p.registrationStatus === 'UNDER_REGISTRATION' && (
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Registration progress</h2>
          <dl className={styles.metaGrid}>
            <div><dt>Business partner</dt><dd>{p.businessPartner ?? '—'}</dd></div>
            <div><dt>Manufacturing model</dt><dd>{p.manufacturingModel ?? '—'}</dd></div>
          </dl>
          {p.raStatus && <div className={styles.descBlock}><strong>RA status</strong><p>{p.raStatus}</p></div>}
        </section>
      )}
    </main>
  );
}
